import React, { useState, useEffect } from 'react';
import './TutorialModal.css';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const steps = [
    {
      title: 'Step 1: Generate Characters',
      description: 'Create unique characters for your story using AI. Click the "+" button in the Characters section and describe your character. The AI will generate consistent character images that you can use throughout your book.',
      images: ['/screenshots/tutorial/tutorial1-1.png', '/screenshots/tutorial/tutorial1-2.png']
    },
    {
      title: 'Step 2: Create Backgrounds',
      description: 'Design beautiful backgrounds and scenes. Click the "+" button in the Backgrounds section and describe the setting. You can create forests, castles, cities, or any scene you imagine.',
      images: ['/screenshots/tutorial/tutorial2-1.png', '/screenshots/tutorial/tutorial2-2.png']
    },
    {
      title: 'Step 3: Compose Scenes',
      description: 'Bring your pages to life! Use the "Compose Scene" button to combine your characters and backgrounds. Select a background, choose characters to include, and let the AI compose a complete scene for your page.',
      images: ['/screenshots/tutorial/tutorial3-1.png', '/screenshots/tutorial/tutorial3-2.png']
    },
    {
      title: 'Step 4: Crop & Style Images',
      description: 'Perfect your images with powerful editing tools. Crop images to focus on what matters, apply feather effects for soft edges, choose different shapes (circle, square, star), and add borders to make your images pop.',
      images: ['/screenshots/tutorial/tutorial4-1.png', '/screenshots/tutorial/tutorial4-2.png']
    },
    {
      title: 'Step 5: AI Image Editing',
      description: 'Edit images with AI-powered tools. Use prompts to modify images (e.g., "add a hat", "change to sunset"), or select specific areas with the lasso tool to edit only that part of the image.',
      images: ['/screenshots/tutorial/tutorial5-1.png', '/screenshots/tutorial/tutorial5-2.png']
    },
    {
      title: 'Step 6: Add Text',
      description: 'Tell your story with beautiful text. Click the text tool to add captions, narration, or dialogue to your scenes. Customize font style, size, and color to match your story\'s mood.',
      images: ['/screenshots/tutorial/tutorial6-1.png', '/screenshots/tutorial/tutorial6-2.png']
    },
    {
      title: 'Step 7: Speech Bubbles',
      description: 'Give your characters a voice! Use the lasso tool to select where you want the speech bubble, then add text with your chosen font style. Perfect for conversations and character expressions.',
      images: ['/screenshots/tutorial/tutorial7-1.png', '/screenshots/tutorial/tutorial7-2.png']
    }
  ];

  // Auto-cycle images every 2 seconds
  useEffect(() => {
    if (!isOpen) return;
    
    const currentStepImages = steps[currentStep].images;
    if (currentStepImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % currentStepImages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [currentStep, isOpen]);

  // Reset image index when step changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [currentStep]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const handleSkip = () => {
    handleClose();
  };

  return (
    <div className="tutorial-overlay" onClick={handleClose}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tutorial-close" onClick={handleClose}>×</button>
        
        <div className="tutorial-header">
          <h2>Welcome to Tiny Little Fox Studio!</h2>
          <p className="tutorial-subtitle">Let's learn how to create your storybook</p>
        </div>

        <div className="tutorial-content">
          <div className="tutorial-step-indicator">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`tutorial-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>

          <div className="tutorial-step">
            <h3>{steps[currentStep].title}</h3>
            <div className="tutorial-image-container">
              <img 
                src={steps[currentStep].images[currentImageIndex]} 
                alt={steps[currentStep].title}
                className="tutorial-image"
              />
              {steps[currentStep].images.length > 1 && (
                <div className="tutorial-image-dots">
                  {steps[currentStep].images.map((_, idx) => (
                    <span 
                      key={idx} 
                      className={`image-dot ${idx === currentImageIndex ? 'active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="tutorial-description">{steps[currentStep].description}</p>
          </div>
        </div>

        <div className="tutorial-footer">
          <button className="tutorial-btn tutorial-btn-secondary" onClick={handleSkip}>
            Skip Tutorial
          </button>
          
          <div className="tutorial-navigation">
            {currentStep > 0 && (
              <button className="tutorial-btn tutorial-btn-outline" onClick={handlePrevious}>
                ← Previous
              </button>
            )}
            <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
              {currentStep < steps.length - 1 ? 'Next →' : 'Get Started'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
